// Get array of fields that are available for use as columns (excludes list fields).  Any fields that are single objects
// are recursively traversed to include columns for those too.  Apply column override details if specified
import getRowMetaData from '../AttributeCellMetaData';
import { IWorkspaceColumnOverride } from '../../@types/workspaceTypes';
import { IAvailableColumn } from './ColumnSelector';

const ATTRIBUTE_CONFIG = 'attributeconfig';
const ATTRIBUTE_VALUES = 'attributevalues';

export const getAvailableColumns = (
  fields: {
    //NOTE - Types here are rough/determined from usage - May be incomplete.
    [key: string]: {
      objectType: string;
      attributeMeta: Array<any>;
      type: 'ProjectedList' | 'Map' | 'Object' | 'Enum'; //May be incomplete
      title?: string;
      name: string;
      overrides: any;
      fields: any;
      validationRules: {
        isTransient: boolean;
      };
      formatter?: any;
      options?: any;
      getRowMetaData?: any;
      sortable?: boolean;
      idList?: Array<any>;

      /**
       * If true, this column will always be displayed (It cannot be disabled via the column selector, but can be moved/re-sized).
       */
      required?: boolean;

      /**
       * Should this column be editable - Pass-through
       */
      editable?: boolean;
    };
  },
  columnOverrides: IWorkspaceColumnOverride,
  fieldPath?: string,
  titlePath?: string
) => {
  if (!fields) {
    return;
  }

  const availableColumns: Array<IAvailableColumn> = [];
  const associatedColumns: any[] = [];
  Object.keys(fields).forEach((fieldName) => {
    const field = fields[fieldName];

    if (columnOverrides[fieldName]?.disabled) {
      return;
    }

    if (field.objectType && field.objectType.substring(0, 5) === 'List<') {
      return;
    }
    if (!field.attributeMeta && (field.type === 'ProjectedList' || field.type === 'Map')) {
      return;
    }

    if (
      (fieldName.toLowerCase().includes(ATTRIBUTE_CONFIG) || fieldName.toLowerCase().includes(ATTRIBUTE_VALUES)) &&
      !/\.\d+$/.test(fieldName)
    ) {
      return;
    }

    const fullFieldName = (fieldPath ? `${fieldPath}.` : '') + fieldName;
    const fullTitle = (titlePath ? `${titlePath}->` : '') + (field.title ? field.title : field.name);

    let column: IAvailableColumn;

    if (field.name === 'RECURSIVE_DEFINITION') {
      return;
    }

    switch (field.type) {
      case 'ProjectedList':
        field.attributeMeta.forEach((attributeTitle, index) => {
          column = {
            title: `${fullTitle}->${attributeTitle}`,
            fieldName: `${fullFieldName}[${index}]`,
            type: 'Object',
            objectType: field.objectType,
            virtualType: 'ProjectedList',
            required: field.required,
            editable: field.editable,
            ...field.overrides,
          };
          associatedColumns.push(column);
          const newAssociatedColumns = getAvailableColumns(
            field.fields,
            columnOverrides,
            column.fieldName,
            column.title
          );
          if (newAssociatedColumns?.length) {
            associatedColumns.push(...newAssociatedColumns);
          }
        });
        break;

      case 'Map':
        let attribFieldName;
        column = {
          title: fullTitle,
          fieldName: fullFieldName,
          type: 'Object',
          objectType: 'MapEntry',
          isTransient: field.validationRules.isTransient,
          required: field.required,
          editable: field.editable,
          ...field.overrides,
        };
        associatedColumns.push(column);
        field.attributeMeta.forEach((attribute) => {
          attribFieldName = `${fullFieldName}[${attribute.id}].${attribute.valueField}`;
          column = {
            name: `${fullTitle}->${attribute.title}`,
            fieldName: attribFieldName,
            type: attribute.classType,
            objectType: null,
            formatter: 'AttributeFormatter',
            getCellMetaData: getRowMetaData,
            required: field.required,
            editable: field.editable,
            ...field.overrides,
          };
          if (columnOverrides && columnOverrides[attribFieldName]) {
            column = Object.assign(column, columnOverrides[attribFieldName]);
          }
          // Reformat details to format needed by table
          column.title = column.name;
          associatedColumns.push(column);
        });
        break;

      case 'Object':
      case 'Enum':
        // Create column details for object level (used for filters)
        column = {
          name: fullTitle,
          fieldName: fullFieldName,
          type: field.type,
          objectType: field.objectType,
          isTransient: field.validationRules.isTransient,
          required: field.required,
          editable: field.editable,
          ...field.overrides,
        };
        column.title = column.name;
        if (columnOverrides && columnOverrides[fullFieldName]) {
          column = Object.assign(column, columnOverrides[fullFieldName]);
        }
        availableColumns.push(column);

        // Add columns for this sub-object
        if (field.fields) {
          const subAssociatedColumns = getAvailableColumns(field.fields, columnOverrides, fullFieldName, fullTitle);
          if (subAssociatedColumns) {
            associatedColumns.push(...subAssociatedColumns);
          }
        }
        break;

      default:
        // Create column details in same format as columnOverrides
        column = {
          name: fullTitle,
          fieldName: fullFieldName,
          type: field.type,
          isTransient: field.validationRules?.isTransient,
          required: field.required,
          editable: field.editable,
          ...field.overrides,
        };

        if (columnOverrides && columnOverrides[fullFieldName]) {
          column = Object.assign(column, columnOverrides[fullFieldName]);
        }
        if (!column.formatter && field.formatter) {
          column = Object.assign(column, {
            formatter: field.formatter,
            options: field.options,
            getRowMetaData: field.getRowMetaData,
          });
        }
        // Reformat details to format needed by table
        column.title = column.name;

        if (field.sortable !== undefined) {
          column.sortable = field.sortable;
        }

        if (!!field.idList) {
          column.idList = field.idList;
        }

        availableColumns.push(column);
    }
  });

  // Add any columns for embedded objects after columns for this object
  if (associatedColumns.length) {
    availableColumns.push(...associatedColumns);
  }

  return availableColumns;
};
